import { Camera } from "lucide-react";
import { openPdfPreview } from "../shared/apiClient";
import type { Report, Materiale } from "../shared/types";
import { STATI_IMPIANTO, CAUSE_GUASTO, ESITI_COMPLETO } from "../shared/constants";
import { SecureImage } from "./SecureImage";

export function ReportInline({
  report,
  squadraNome,
  onPreview,
}: {
  report: Report;
  squadraNome?: string;
  onPreview?: (url: string, nome?: string) => void;
}) {
  let materiali: Materiale[] = [];
  try {
    materiali = typeof report.materiali === 'string'
      ? JSON.parse(report.materiali || '[]')
      : (Array.isArray(report.materiali) ? report.materiali : []);
  } catch {
    materiali = [];
  }

  let fotoList: string[] = [];
  try {
    fotoList = typeof report.foto === 'string'
      ? JSON.parse(report.foto || '[]')
      : (Array.isArray(report.foto) ? report.foto : []);
  } catch {
    fotoList = [];
  }

  const causas = report.causaGuasto
    ? (CAUSE_GUASTO.find((c) => c.value === report.causaGuasto)?.label || report.causaGuasto) +
      (report.causaGuastoAltro ? ` (${report.causaGuastoAltro})` : '')
    : null;

  const effettivoSquadraNome = report.squadraNome || squadraNome;
  const autore = report.redattoDa === 'operatore'
    ? (effettivoSquadraNome ? `Operatore (su ${effettivoSquadraNome})` : 'Operatore')
    : (effettivoSquadraNome || 'Squadra');

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-2xs space-y-2.5">
      <div className="flex items-center justify-between gap-3 pb-2 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs">
            📋
          </div>
          <span className="font-bold text-sm text-slate-900">{autore}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400 font-medium">
            {report.chiusoAt ? new Date(report.chiusoAt).toLocaleString('it-IT') : 'In corso'}
          </span>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition active:scale-95"
            onClick={() => openPdfPreview(`/chiamate/${report.chiamataId}/report/${report.id}/pdf`)}
            title="Visualizza o stampa PDF report"
          >
            🖨 PDF
          </button>
        </div>
      </div>

      <div className="text-xs sm:text-sm space-y-1.5 text-slate-700">
        {report.redattoDa === 'operatore' ? (
          <>
            <div><span className="font-bold text-slate-500">Descrizione:</span> {report.descrizione}</div>
            {report.esito && <div><span className="font-bold text-slate-500">Esito:</span> <span className="badge-verde">{(ESITI_COMPLETO[report.esito] || {}).label || report.esito}</span></div>}
          </>
        ) : (
          <>
            {report.statoImpiantoArrivo && (
              <div>
                <span className="font-bold text-slate-500">Stato impianto arrivo:</span>{' '}
                <span className="font-semibold text-slate-800">
                  {STATI_IMPIANTO[report.statoImpiantoArrivo] || report.statoImpiantoArrivo}
                  {report.statoImpiantoAltro ? ` (${report.statoImpiantoAltro})` : ''}
                </span>
              </div>
            )}
            {causas && (
              <div>
                <span className="font-bold text-slate-500">Causa guasto:</span>{' '}
                <span className="font-semibold text-slate-800">{causas}</span>
              </div>
            )}
            {report.azioniEseguite && (
              <div>
                <span className="font-bold text-slate-500">Azioni eseguite:</span>{' '}
                <span className="text-slate-800">{report.azioniEseguite}</span>
              </div>
            )}
            <div className="flex items-center gap-4 flex-wrap text-xs text-slate-500 pt-1">
              {report.inizioLavoroAt && (
                <span>Inizio: <b className="text-slate-700">{new Date(report.inizioLavoroAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</b></span>
              )}
              {report.fineLavoroAt && (
                <span>Fine: <b className="text-slate-700">{new Date(report.fineLavoroAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</b></span>
              )}
            </div>
            {materiali.length > 0 && (
              <div className="pt-1">
                <span className="font-bold text-slate-500">Materiali impiegati:</span>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {materiali.map((m, i) => (
                    <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold border border-slate-200">
                      {m.nome || m.descrizione || 'Materiale'}{m.quantita && String(m.quantita).trim() !== '' ? ` (${m.quantita})` : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {report.esito && (
              <div className="pt-1">
                <span className="font-bold text-slate-500">Esito:</span>{' '}
                <span className={report.esito === 'risolto' || report.esito === 'lasciato_acceso_colori' || report.esito === 'lasciato_acceso_lampeggio' || report.esito === 'lasciato_spento' ? 'badge-verde' : 'badge-amber'}>
                  {(ESITI_COMPLETO[report.esito] || {}).label || report.esito}
                </span>
              </div>
            )}
            {fotoList.length > 0 && (
              <div className="pt-2 border-t border-slate-100 space-y-1.5">
                <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  <Camera className="w-3.5 h-3.5 text-slate-600" />
                  <span>Foto Intervento ({fotoList.length})</span>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 pt-0.5">
                  {fotoList.map((fn, idx) => (
                    <div
                      key={idx}
                      className="relative group rounded-xl border border-slate-200/90 bg-slate-50 overflow-hidden aspect-square block shadow-2xs hover:shadow-md transition cursor-pointer"
                      onClick={() => onPreview?.(`/api/uploads/${fn}`, `Foto report ${effettivoSquadraNome || ''} (${idx + 1}/${fotoList.length})`)}
                    >
                      <SecureImage
                        src={`/api/uploads/${fn}`}
                        alt={`Foto intervento ${idx + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        onImageClick={(blobUrl) => {
                          onPreview?.(blobUrl, `Foto report ${effettivoSquadraNome || ''} (${idx + 1}/${fotoList.length})`);
                        }}
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] font-bold pointer-events-none">
                        🔍 Zoom
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </article>
  );
}
